import React, {
  FC, useState, useMemo, useLayoutEffect,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from 'antd';
import type { StepPageProps } from '../../../assessment-portal';
import { useAssessmentPortalState } from '../../../assessment-portal';
import { ADHD_BASE, AdhdQuestionnaireFlowStep } from '../../data/constants';
import { YOUNG_QUESTIONNAIRE_ENABLED } from '../../config/feature-flags';
import {
  hasBookingConfirmationSnapshot,
  loadBookingConfirmationSnapshot,
} from '../../utils/booking-confirmation-snapshot';
import { formatAppointmentDueDate } from '../../utils/appointment-display';
import {
  loadValidQuestionnaireDraft,
  saveQuestionnaireDraft,
  clearQuestionnaireDraft,
} from '../../utils/questionnaire-draft';
import { getQuestionnaireTemplateConfig } from '../../utils/questionnaire-variant';
import type { AdhdQuestionnaireFlowState } from '../../types/flow';
import AssessmentIntroPage from '../assessment-intro-page';
import YoungAssessmentIntroPage from '../young-assessment-intro-page';

// antd 4 animates the modal mask through findDOMNode, which warns under StrictMode;
// the mask appears without a fade, the dialog keeps its animation.
const NO_MASK_TRANSITION = '';

const AssessmentIntroStep: FC<StepPageProps> = ({ onNext }) => {
  const navigate = useNavigate();
  const { flowState, goToStep } = useAssessmentPortalState<AdhdQuestionnaireFlowState>();
  const { ageRange } = flowState;
  // Without an age range the adult template id is only a placeholder; the effect below redirects.
  const { templateId } = getQuestionnaireTemplateConfig(ageRange ?? 'adult');

  useLayoutEffect(() => {
    if (!ageRange) {
      goToStep(AdhdQuestionnaireFlowStep.AGE_RANGE);
    }
  }, [ageRange, goToStep]);

  const dueDateLabel = useMemo(() => {
    const snapshot = loadBookingConfirmationSnapshot();
    if (!snapshot?.startDateTime) return '';
    return formatAppointmentDueDate(snapshot.startDateTime);
  }, []);
  const validDraft = useMemo(() => loadValidQuestionnaireDraft(templateId), [templateId]);

  const [showResumeModal, setShowResumeModal] = useState(!!validDraft);
  const [showSaveModal, setShowSaveModal] = useState(false);

  const handleStart = () => {
    // Check fresh on click in case draft appeared since mount
    const draft = loadValidQuestionnaireDraft(templateId);
    if (draft) {
      setShowResumeModal(true);
    } else {
      setShowSaveModal(true);
    }
  };

  const handleResumeContinue = () => {
    setShowResumeModal(false);
    goToStep(AdhdQuestionnaireFlowStep.ASSESSMENT_QUESTIONNAIRE);
  };

  const handleResumeStartOver = () => {
    clearQuestionnaireDraft(templateId);
    setShowResumeModal(false);
    setShowSaveModal(true);
  };

  const handleSaveYes = () => {
    // Save an empty draft as the opt-in marker
    saveQuestionnaireDraft(templateId, {
      answers: {},
      questionIndex: 0,
      explicitlySaved: true,
    });
    setShowSaveModal(false);
    onNext({});
  };

  const handleSaveNo = () => {
    setShowSaveModal(false);
    onNext({});
  };

  if (!ageRange) {
    return null;
  }

  const handleBack = () => goToStep(AdhdQuestionnaireFlowStep.AGE_RANGE);
  // Without the age range page the intro keeps its released Back: to /booked when booked.
  const canGoBackToBooked = hasBookingConfirmationSnapshot();
  const adultIntroBack = YOUNG_QUESTIONNAIRE_ENABLED
    ? { showBack: true, onBack: handleBack }
    : {
      showBack: canGoBackToBooked,
      onBack: canGoBackToBooked ? () => navigate(`${ADHD_BASE}/booked`) : undefined,
    };

  return (
    <>
      {ageRange === 'young' ? (
        <YoungAssessmentIntroPage
          onBack={handleBack}
          onNext={handleStart}
          dueDateLabel={dueDateLabel}
        />
      ) : (
        <AssessmentIntroPage
          showBack={adultIntroBack.showBack}
          onBack={adultIntroBack.onBack}
          onNext={handleStart}
          dueDateLabel={dueDateLabel}
        />
      )}

      {/* Resume modal — shown immediately if a valid draft exists */}
      <Modal
        visible={showResumeModal}
        maskTransitionName={NO_MASK_TRANSITION}
        onCancel={() => setShowResumeModal(false)}
        title={<span className="text-base font-bold text-brand-dark">Welcome back!</span>}
        footer={null}
        closable
        width={420}
        centered
      >
        <p className="text-base leading-relaxed text-slate-500 mt-2 mb-5">
          Looks like you were partway through — continue from where you left off?
        </p>
        <div className="flex flex-col gap-2.5">
          <button
            type="button"
            onClick={handleResumeContinue}
            className="w-full px-5 py-2.5 rounded-xl bg-brand text-white font-semibold text-sm hover:opacity-90 transition-opacity cursor-pointer"
          >
            Continue
          </button>
          <button
            type="button"
            onClick={handleResumeStartOver}
            className="w-full px-5 py-2.5 rounded-xl border border-slate-200 text-slate-500 font-medium text-sm hover:bg-slate-50 transition-colors cursor-pointer"
          >
            Start over
          </button>
        </div>
      </Modal>

      {/* Save-preference modal — shown when a new user clicks Start */}
      <Modal
        visible={showSaveModal}
        maskTransitionName={NO_MASK_TRANSITION}
        onCancel={() => setShowSaveModal(false)}
        title={<span className="text-base font-bold text-brand-dark">Save your progress?</span>}
        footer={null}
        closable
        width={420}
        centered
      >
        <p className="text-base leading-relaxed text-slate-500 mt-2 mb-5">
          Your answers can be saved on this device for 15 days so you can
          return and continue where you left off.
        </p>
        <p className="text-sm leading-relaxed text-slate-400 mb-5">
          Progress is saved only on this browser — it won&rsquo;t be available on
          other devices. If this is a shared or public computer, choose
          &ldquo;No thanks&rdquo; to protect your privacy.
        </p>
        <div className="flex flex-col gap-2.5">
          <button
            type="button"
            onClick={handleSaveYes}
            className="w-full px-5 py-2.5 rounded-xl bg-brand text-white font-semibold text-sm hover:opacity-90 transition-opacity cursor-pointer"
          >
            Yes, save for 15 days
          </button>
          <button
            type="button"
            onClick={handleSaveNo}
            className="w-full px-5 py-2.5 rounded-xl border border-slate-200 text-slate-500 font-medium text-sm hover:bg-slate-50 transition-colors cursor-pointer"
          >
            No thanks, don&rsquo;t save
          </button>
        </div>
      </Modal>
    </>
  );
};

export default AssessmentIntroStep;
